import {
  ElementPropertyName,
  ElementPropertyType,
  IFactoryExpressionProperty,
} from '../../models/element-property.model';
import { Coordinate } from '@shared/models/geography.model';
import { IProduct } from '../../models/product.model';
import {
  ActivityEnum,
  FutureActivities,
} from '@shared/models/activities.model';
import { ActivityId } from '@shared/models/activity-class.model';
import { ElementCategoryID } from '@shared/models/element-categories.model';
import { ActivityType } from '@shared/models/design/activities/activity-type.model';

/**
 * Element categories affected by space demand calculations
 */
export const SPACE_DEMAND_CATEGORIES = Object.freeze([
  ElementCategoryID.Heating,
  ElementCategoryID.Cooling,
  ElementCategoryID.AirHandlingUnit,
] as const);

/**
 * Absolute zero temperature in °C
 * See: https://en.wikipedia.org/wiki/Absolute_zero
 */
export const ABSOLUTE_ZERO_TEMP = -273.15; // °C

/**
 * Any kind of activity-ids used in the app (that we should support).
 * Mostly to minimize the amount of different activity-ids we need to use for energy calculations.
 * TODO: Take control of activities
 */
export type ActivityInput =
  ActivityId | EnergyActivity | ActivityEnum | ActivityType | FutureActivities;

/**
 * The different activities supported by energy calculations.
 * Should be replaced an activity mix based on activities
 */
export type EnergyActivity =
  | ActivityType.Residential
  | ActivityType.Office
  | ActivityType.School
  | ActivityType.Other
  | ActivityType.Garage;

export const uValueProperty: IFactoryExpressionProperty = {
  name: ElementPropertyName.ThermalTransmittance,
  displayName: 'Thermal transmittance (U)',
  unit: 'W/m²K',
  type: ElementPropertyType.Expression,
  fallbackCount: '0',
  description:
    'Thermal transmittance, U (W/m²K) of the element. Used to calculate heating demand of the building',
};

/**
 * Options used for most heating calculations
 */
export type IHeatingEnergyCalculationOptions = {
  /**
   * The indoor temperature in °C.
   */
  indoorTemp?: number;

  /**
   * Which outdoor temperature (°C) to we want to use in our calculations.
   */
  outdoorTemp?: number;

  /**
   * The location of the building.
   *
   * NOTE: will probably be removed. put it back to make it easier to resolve some merge conflicts
   */
  location?: Coordinate;

  /**
   * The number of degree hours for the location.
   * If not provided, will be calculated from the location and dimensioning temp.
   */
  degreeHours?: number;
};

/**
 * Documents used for the calculations can be found here:
 * https://drive.google.com/drive/u/1/folders/1UNmR100WY4a-PMj-AnJFfhAyrVw1zgiB
 */

/**
 * Air density in kg/m³
 */
export const AIR_DENSITY = 1.2; // kg / m³

/**
 * CP_AIR stands for "Specific Heat Capacity of Air" and is measured in J/(kg·K).
 * It represents the amount of energy required to raise the temperature of 1 kg of air by 1 Kelvin (or 1°C).
 * The value 1006 J/(kg·K) is the standard value for air at normal atmospheric pressure and room temperature.
 */
export const CP_AIR = 1006; // J/(kg·K)

/**
 * Default vent flow per ventilated area (m³/m²s)
 */
export const DEFAULT_VENT_FLOW_PER_M2 = 0.35 / 1000;

/**
 * Default indoor temperature.
 * 21°C is the default indoor temperature in Sweden.
 * See: https://www.boverket.se/Resources/constitutiontextstore/BEN/xml/Konsoliderad_BEN_BFS_2016_12.pdf
 */
export const DEFAULT_INDOOR_TEMP = 21; // °C

/**
 * The limit temperature is the temperature at which a building does not need any heating to maintain a comfortable indoor temperature. It is calculated by taking into account the building's heat losses and internal heat sources, such as solar radiation and heat from electrical appliances or people.
 */
export const DEFAULT_LIMIT_TEMP = 16; // °C

/**
 * The temperature at which a building need cooling to maintain a comfortable indoor temperature.
 * It is calculated by taking into account the building's heat losses and internal heat sources, such as solar radiation and heat from electrical appliances or people.
 */
export const TEMP_OUTSIDE_COOLING_HOURS = 25; // °C

/**
 * Tap water recirculation loss per meter of pipe (W/m).
 * From klockaren (NCC-report)
 */
export const TAP_WATER_RECIRCULATION_LOSS = 8; // W/m

/**
 * Hur mycket av tap water loss anses bli space heating effect? (resultatet kan endast tillgodoräknas under eldningssäsongen)
 */
export const TAP_WATER_RECIRCULATION_LOSS_HEATING_GAIN_FACTOR = 0.7;

/**
 * Activity energy consumption per heated area (kWh/m²/year)
 * Source: https://www.boverket.se/Resources/constitutiontextstore/BEN/xml/Konsoliderad_BEN_BFS_2016_12.pdf
 */
export const ACTIVITY_ELECTRICITY_CONSUMPTION_PER_HEATED_AREA: Record<
  EnergyActivity,
  number
> = {
  [ActivityType.Residential]: 21, // kWh/m²/year från: https://www.sveby.org/wp-content/uploads/2024/11/Sveby-brukarindata-bostadshus-2.0-241111.pdf
  [ActivityType.Office]: 50, // kWh/m²/year
  [ActivityType.School]: 20, // kWh/m²/year (kitchen 24 other area 14)
  [ActivityType.Garage]: 21, // kWh/m²/year TODO: Replace with actual value not apartment value
  [ActivityType.Other]: 21, // kWh/m²/year TODO: Replace with actual value not apartment value
};

/**
 * How much of the activity electricity we can assume will heat the building
 * Page 4-5: https://www.boverket.se/Resources/constitutiontextstore/BEN/xml/Konsoliderad_BEN_BFS_2016_12.pdf
 */
export const ACTIVITY_ELECTRICY_HEATING_GAIN_FACTOR: Record<
  EnergyActivity,
  number
> = {
  [ActivityType.Residential]: 0.7,
  [ActivityType.Office]: 1,
  [ActivityType.School]: 1,
  [ActivityType.Garage]: 0.7, // TODO: Replace with actual value not apartment value
  [ActivityType.Other]: 0.7, // TODO: Replace with actual value not apartment value
};

/**
 * Assumption: BOA/Atemp = 0.85 for Swedish multi-family housing (FBH).
 * Meaning: About 85% of the heated floor area (Atemp, per BBR) is usable apartment area (BOA);
 * the remaining ~15% is heated common/technical space (stairwells, corridors, laundry, storage, plant rooms).
 * Use: Early-stage estimates when detailed plans are not available.
 * Typical range: 0.82–0.88. Adjust down for corridor-house typologies (~0.72–0.80) or care housing with large shared areas (~0.60–0.75);
 * adjust up for compact stairwell buildings (~0.88–0.90).
 * Caveat: Excludes unheated areas (e.g., garages). Replace with measured areas once drawings are fixed.
 */
export const DEFAULT_LIVINGAREA_PER_HEATED_AREA_RATIO = 0.85; // 85%

/**
 * Heating demand due to airing per m2 (kWh/m²/year)
 * Source: https://www.boverket.se/Resources/constitutiontextstore/BEN/xml/Konsoliderad_BEN_BFS_2016_12.pdf
 */
export const AIRING_HEATING_COMSUMPTION_PER_HEATED_AREA = 4; // kWh/m²/year

/**
 * The extra heating demand due to ineffective regulation/steering of the heating system (reglerförluster).
 */
export const REGULATION_LOSS_FACTOR = 0.1;

/**
 * Heating demand for tapwater per year depending on the activity type (kWh/m²/year).
 */
export const TAPWATER_HEATING_DEMAND = Object.freeze({
  /**
   * Heating demand for small houses.
   * Page 4: https://rinfo.boverket.se/BFS2016-12/pdf/BFS2017-6.pdf
   */
  SMALL_HOUSE: 20,
  /**
   * Heating demand for apartments.
   * Page 5: https://rinfo.boverket.se/BFS2016-12/pdf/BFS2017-6.pdf
   */
  APARTMENT: 25,

  /**
   * Heating demand for offices.
   * Page 6: https://rinfo.boverket.se/BFS2016-12/pdf/BFS2017-6.pdf
   */
  OFFICE: 2,

  /**
   * Heating demand for schools.
   * Page 7-9: https://rinfo.boverket.se/BFS2016-12/pdf/BFS2017-6.pdf
   */
  SCHOOL: 2,
});

/**
 * The g-value (sometimes also called a Solar Factor or Total Solar Energy Transmittance) is the coefficient commonly used in Europe to measure the solar energy transmittance of windows.
 * Despite having minor differences in modeling standards compared to the SHGC, the two values are effectively the same. A g-value of 1.0 represents full transmittance of all solar radiation while 0.0 represents a window with no solar energy transmittance.
 * In practice though, most g-values will range between 0.2 and 0.7, with solar control glazing having a g-value of less than 0.5
 * See: https://en.wikipedia.org/wiki/Solar_gain
 */
export const SOLAR_HEAT_GAIN_COEFFICIENT = 0.52;

/**
 * Solar shading coefficient  (Solavskärmning). How solar energy is left after curtains etc have "screened" the sun.
 * 0 means that all sun light is blocked, 1 means that no sun light is blocked.
 * Source beteendestyrd: https://www.boverket.se/Resources/constitutiontextstore/BEN/xml/Konsoliderad_BEN_BFS_2016_12.pdf
 * Source sammanlagd avskärmning - sid 11: https://www.sveby.org/wp-content/uploads/2024/11/Sveby-brukarindata-bostadshus-2.0-241111.pdf
 */
export const SOLAR_SCREEN_FACTOR = 0.5;

/**
 * How much of the window area is glass
 */
export const WINDOW_GLASS_AREA_PERCENTAGE = 0.85;

/**
 * Thermal bridges loss factor (köldbryggor)
 * See page 53 (55): https://www.boverket.se/globalassets/publikationer/dokument/2012/handbok-for-energihushallning-enligt-boverkets-byggregler.pdf
 * 20% schablon fr BV 2012. punktformiga KB kan ignoreras (BV 2003) https://www.diva-portal.org/smash/get/diva2:1232918/FULLTEXT01.pdf
 */
export const THERMAL_BRIDGES_LOSS_FACTOR = 0.2;

/**
 * Air leakage (l/s·m²) at +-50 Pa pressure difference
 * Air leakage is a central parameter in energy calculations according to BEN 3.
 * It describes how much air unintentionally passes through the building's climate envelope (walls, roof, floor) at a certain pressure difference, usually ±50 Pascal (Pa).
 * Air leakage is specified in the unit liters per second and square meter (l/s·m²).
 * Maximum air leakage based on BBR 29 requirements
 * Small houses: ≤ 0.6 l/s·m² at 50 Pa
 * Level A: ≤ 0.40 l/s·m² at 50 Pa (recommended)
 * Level B: ≤ 0.60 l/s·m² at 50 Pa (minimum requirement)
 * Passive house: ≤ 0.3 l/s·m² at 50 Pa (stricter requirement, not BEN 3)
 * Source: https://www.boverket.se/Resources/constitutiontextstore/BEN/xml/Konsoliderad_BEN_BFS_2016_12.pdf
 */
export const AIR_LEAKAGE_Q50 = 0.4;

/**
 * Wind and pressure varies a lot in different climates.
 * This is an estimated factor to adjust the air leakage based on the climate.
 * 0.05 means that the air leakage is 5% higher in the climate with the lowest air leakage.
 */
export const AIR_LEAKAGE_CLIMATE_FACTOR = 0.05;

export interface ICityClimateData extends Coordinate {
  city: string;
  temp: number;
  /**
   * Average number of sun hours per year.
   * Can be found here: https://www.smhi.se/kunskapsbanken/meteorologi/stralning/solstralning-i-sverige
   */
  sunHours: number;

  /**
   * Average solar insolation (directinstrålning) per year (kWh/m²)
   * See: https://www.smhi.se/kunskapsbanken/meteorologi/stralning/solstralning-i-sverige
   */
  solarInsolation?: number;

  /**
   * "Design outdoor temperature (DOT)". In Swedish: "Dimensionerande utetemperatur (DUT)" or "Dimensionerande vinterutetemperatur (DVUT)".
   * This is the lowest outdoor temperature that the heating system is designed for. It varies depending on where in Sweden the building is located and the building's time constant (how quickly the building reacts to temperature changes).
   * Currently we use time constant 10 days.
   * Some examples of DUT/DVUT for different Swedish cities (for a normal building with a 1-day time constant):
   * Stockholm: cirka -15°C
   * Göteborg: cirka -12°C
   * Malmö: cirka -10°C
   * Kiruna: cirka -28°C
   * Boverket regarding DUT/DVUT: https://www.boverket.se/sv/PBL-kunskapsbanken/regler-om-byggande/boverkets-byggregler/termiskt-klimat/
   * List with DUT/DVUT values: https://www.boverket.se/sv/om-boverket/publicerat-av-boverket/oppna-data/dimensionerande-vinterutetemperatur-dvut-1981-2010/
   */
  dot: number;

  /**
   * Geographical adjustment factor (Fgeo).
   * Page 143: https://www.boverket.se/globalassets/publikationer/dokument/2020/konsoliderad-bbr-2011-6-tom-2020-4.pdf
   */
  fGeo: number;

  /**
   * Number of hours per year when the outdoor temperature exceeds 25°C (TEMP_OUTSIDE_COOLING_HOURS),
   * requiring cooling for office buildings. This is an estimated value based on climate data
   * and typical office building characteristics.
   */
  coolingHours: number;
}

export const ENERGY_CLASSES = ['A', 'B', 'C', 'D', 'E', 'F', 'G'] as const;
export type EnergyClass = (typeof ENERGY_CLASSES)[number];

/**
 * Colors for the energy classes. Pipetted from the Boverket website example pictures.
 */
export const ENERGY_CLASS_COLORS: Record<EnergyClass, `#${string}`> = {
  A: '#018f36',
  B: '#55ab26',
  C: '#cbd302',
  D: '#ffee02',
  E: '#fbb902',
  F: '#eb6909',
  G: '#e20018',
};

// K = Kelvin
// R = Thermal resistance (K/W)
// U = Thermal transmittance (W/m²K)
// C = Therman conductance (K/W)

export enum BUILDING_ENVELOPE_TYPE {
  WALL = 1,
  ROOF = 2,
  FOUNDATION = 3,
  WINDOW = 4,
}

export type IThermalProduct = IProduct & {
  /**
   * For repeating quantity, share is the share of the total space a layer occupies.
   */
  share: number;

  mass: number;
};

export interface IThermalLayer {
  name?: string;

  /**
   * Thickness in meters
   */
  thickness: number;

  /**
   * Products included in the layer (and their share of the layer)
   */
  products: IThermalProduct[];

  /**
   * U-value (W/m²K)
   */
  thermalTransmittance: number;

  /**
   * Area in m², area_side for wall, area_top for slab.
   */
  area: number;

  /**
   * Mass per m²
   */
  massPerArea: number;

  /**
   * m2 (J/m²/°C)
   */
  heatCapacity: number;

  heatEnergyPerArea: number;
}

export type HeatPumpAuxilaryEffectRatio = {
  effect: number;
  energy: number;
};

/**
 * For air to air they say that if you cover 70% of the peak effect with the heat pump that is 95% of the energy consumption.
 * Another case is 50% effect results in 90% of the energy consumption.
 */
export const HEAT_PUMP_EFFECT_RATIO: HeatPumpAuxilaryEffectRatio = {
  effect: 0.5,
  energy: 0.9,
};
